import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const CATEGORIES = ['student', 'resident', 'staff', 'visitor'];
const CATEGORY_COLORS = { student: 'sky', resident: 'violet', staff: 'rose', visitor: 'orange' };

export default function Cardholders({ isAdmin }) {
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [form, setForm] = useState({
    full_name: '', category: 'student', email: '', phone: '', national_id: '',
    building: '', department: '', accommodation_start: '', accommodation_end: '',
    meals_per_day: 3, is_active: true,
  });
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => { fetchCardholders(); }, []);

  const fetchCardholders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('restaurant_cardholders')
        ?.select('*')
        ?.order('full_name');
      if (!error) setCardholders(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ full_name: '', category: 'student', email: '', phone: '', national_id: '', building: '', department: '', accommodation_start: '', accommodation_end: '', meals_per_day: 3, is_active: true });
    setEditingId(null);
    setErrorMsg('');
  };

  const openEdit = (c) => {
    setForm({
      full_name: c?.full_name || '',
      category: c?.category || 'student',
      email: c?.email || '',
      phone: c?.phone || '',
      national_id: c?.national_id || '',
      building: c?.building || '',
      department: c?.department || '',
      accommodation_start: c?.accommodation_start || '',
      accommodation_end: c?.accommodation_end || '',
      meals_per_day: c?.meals_per_day || 3,
      is_active: c?.is_active ?? true,
    });
    setEditingId(c?.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form?.full_name?.trim()) { setErrorMsg('Full name is required'); return; }
    setSaving(true);
    setErrorMsg('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = { ...form, meals_per_day: Number(form?.meals_per_day) || 3 };
      if (!editingId) payload.created_by = user?.id;
      const { error } = editingId
        ? await supabase?.from('restaurant_cardholders')?.update(payload)?.eq('id', editingId)
        : await supabase?.from('restaurant_cardholders')?.insert(payload);
      if (error) { setErrorMsg(error?.message); return; }
      setShowForm(false);
      resetForm();
      fetchCardholders();
    } catch (e) { setErrorMsg(e?.message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (id, current) => {
    await supabase?.from('restaurant_cardholders')?.update({ is_active: !current })?.eq('id', id);
    fetchCardholders();
  };

  const filtered = cardholders?.filter(c => {
    const matchCat = filterCategory === 'all' || c?.category === filterCategory;
    if (!searchQuery) return matchCat;
    const q = searchQuery?.toLowerCase();
    return matchCat && (c?.full_name?.toLowerCase()?.includes(q) || c?.email?.toLowerCase()?.includes(q) || c?.national_id?.toLowerCase()?.includes(q));
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex gap-2 flex-wrap">
          <input type="text" placeholder="Search cardholders..." value={searchQuery} onChange={e => setSearchQuery(e?.target?.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm bg-background text-foreground focus:outline-none w-52" />
          <select value={filterCategory} onChange={e => setFilterCategory(e?.target?.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm bg-background text-foreground focus:outline-none">
            <option value="all">All Categories</option>
            {CATEGORIES?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
          </select>
        </div>
        {isAdmin && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-all">
            <Icon name="UserPlus" size={15} />Add Cardholder
          </button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-foreground">{editingId ? 'Edit Cardholder' : 'Add Cardholder'}</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            {errorMsg && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{errorMsg}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Full Name *</label>
                <input value={form?.full_name} onChange={e => setForm(p => ({ ...p, full_name: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
                <select value={form?.category} onChange={e => setForm(p => ({ ...p, category: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none">
                  {CATEGORIES?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Meals/Day</label>
                <input type="number" min={1} max={3} value={form?.meals_per_day} onChange={e => setForm(p => ({ ...p, meals_per_day: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
                <input type="email" value={form?.email} onChange={e => setForm(p => ({ ...p, email: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Phone</label>
                <input value={form?.phone} onChange={e => setForm(p => ({ ...p, phone: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">National ID</label>
                <input value={form?.national_id} onChange={e => setForm(p => ({ ...p, national_id: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Building</label>
                <input value={form?.building} onChange={e => setForm(p => ({ ...p, building: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Department</label>
                <input value={form?.department} onChange={e => setForm(p => ({ ...p, department: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Accommodation Start</label>
                <input type="date" value={form?.accommodation_start} onChange={e => setForm(p => ({ ...p, accommodation_start: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Accommodation End</label>
                <input type="date" value={form?.accommodation_end} onChange={e => setForm(p => ({ ...p, accommodation_end: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={form?.is_active} onChange={e => setForm(p => ({ ...p, is_active: e?.target?.checked }))}
                  className="rounded border-border text-amber-500" />
                <label htmlFor="is_active" className="text-sm text-foreground">Active</label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setShowForm(false); resetForm(); }}
                className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-all">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-all">
                {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                {editingId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Icon name="Loader2" size={22} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Category</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Building / Dept</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Accommodation</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Meals/Day</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Status</th>
                  {isAdmin && <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(c => (
                  <tr key={c?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-medium text-foreground">{c?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{c?.email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold capitalize bg-${CATEGORY_COLORS?.[c?.category]}-100 text-${CATEGORY_COLORS?.[c?.category]}-700`}>
                        {c?.category}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground text-xs">
                      {c?.building || '—'}{c?.department ? ` / ${c?.department}` : ''}
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">
                      {c?.accommodation_start ? `${c?.accommodation_start} → ${c?.accommodation_end || '∞'}` : '—'}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold text-foreground">{c?.meals_per_day}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${c?.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {c?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Edit">
                            <Icon name="Pencil" size={13} />
                          </button>
                          <button onClick={() => toggleActive(c?.id, c?.is_active)}
                            className={`p-1.5 rounded-lg hover:bg-muted transition-colors ${c?.is_active ? 'text-amber-500' : 'text-emerald-500'}`}
                            title={c?.is_active ? 'Deactivate' : 'Activate'}>
                            <Icon name={c?.is_active ? 'UserX' : 'UserCheck'} size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered?.length === 0 && (
              <div className="py-12 text-center">
                <Icon name="Users" size={28} className="text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No cardholders found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
